import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { ThemeContext } from '../../../../context/ThemeContext';
import { postData, getData, serverURL } from '../../../../services/Fetchnodeservices';

const WebContentManager = () => {
  const { theme: globalTheme, changeTheme, themeName, themes } = useContext(ThemeContext);

  // Map global theme to local theme object for all existing styles
  const theme = useMemo(() => ({
    bgPrimary: globalTheme.background,
    bgSecondary: globalTheme.surface,
    bgTertiary: globalTheme.surfaceLighter || `${globalTheme.primary}08`,
    bgCard: globalTheme.surface,
    bgInput: globalTheme.surfaceLighter || `${globalTheme.primary}05`,
    bgHover: globalTheme.surfaceHover || `${globalTheme.primary}08`,
    textPrimary: globalTheme.textPrimary,
    textSecondary: globalTheme.textSecondary,
    textMuted: globalTheme.textMuted,
    textLight: globalTheme.textSecondary,
    border: globalTheme.border,
    borderLight: globalTheme.border,
    borderFocus: globalTheme.primary,
    primary: globalTheme.primary,
    primaryDark: globalTheme.primary,
    primaryLight: `${globalTheme.primary}15`,
    primaryGradient: `linear-gradient(135deg, ${globalTheme.primary} 0%, ${globalTheme.primary}cc 100%)`,
    success: globalTheme.success,
    successBg: `${globalTheme.success}15`,
    warning: globalTheme.warning,
    warningBg: `${globalTheme.warning}15`,
    danger: globalTheme.error || globalTheme.danger,
    dangerBg: `${globalTheme.error || globalTheme.danger}15`,
    info: globalTheme.info || globalTheme.primary,
    infoBg: `${globalTheme.primary}15`,
    shadow: `0 8px 30px ${globalTheme.primary}15`,
    shadowHover: `0 12px 40px ${globalTheme.primary}25`,
    videoBg: globalTheme.background,
    posterBorder: globalTheme.border,
    scrollbarTrack: globalTheme.border,
    scrollbarThumb: globalTheme.primary,
    scrollbarThumbHover: globalTheme.primary,
  }), [globalTheme]);

  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showForm, setShowForm] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [statusLoading, setStatusLoading] = useState(null);
  const videoRefs = useRef({});
  
  const [formData, setFormData] = useState({
    video: null,
    poster: null,
    title1: '',
    subtitle1: '',
    title2: '',
    subtitle2: '',
    header: ''
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const response = await getData("Headercontent/fetch_webcontent");
      if (response && response.status) {
        setContentList(response.data || []);
        setMessage({ text: 'Content loaded successfully', type: 'success' });
      } else {
        setMessage({ text: response?.message || 'Failed to load content', type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setMessage({ text: 'Server error while fetching content', type: 'error' });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData(prev => ({ ...prev, [name]: files[0] || null }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      video: null, poster: null,
      title1: '', subtitle1: '', title2: '', subtitle2: '', header: ''
    });
    const videoInput = document.getElementById('videoInput');
    const posterInput = document.getElementById('posterInput');
    if (videoInput) videoInput.value = '';
    if (posterInput) posterInput.value = '';
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const formDataToSend = new FormData();
    
    if (formData.video) formDataToSend.append('video', formData.video);
    if (formData.poster) formDataToSend.append('poster', formData.poster);
    
    formDataToSend.append('title1', formData.title1 || '');
    formDataToSend.append('subtitle1', formData.subtitle1 || '');
    formDataToSend.append('title2', formData.title2 || '');
    formDataToSend.append('subtitle2', formData.subtitle2 || '');
    formDataToSend.append('header', formData.header || '');

    try {
      const response = await postData("Headercontent/add_webcontent", formDataToSend, true);
      if (response && response.status) {
        setMessage({ text: `${response.message} (ID: ${response.insertId || 'N/A'})`, type: 'success' });
        resetForm();
        await fetchContent();
      } else {
        setMessage({ text: response?.message || 'Failed to add content', type: 'error' });
      }
    } catch (error) {
      console.error('Error adding content:', error);
      setMessage({ text: 'Server error while adding content: ' + error.message, type: 'error' });
    } finally {
      setSubmitting(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    }
  };

  const handleUpdate = async (id) => {
    try {
      const formDataToSend = new FormData();
      if (formData.video) formDataToSend.append('video', formData.video);
      if (formData.poster) formDataToSend.append('poster', formData.poster);
      
      formDataToSend.append('title1', formData.title1 || '');
      formDataToSend.append('subtitle1', formData.subtitle1 || '');
      formDataToSend.append('title2', formData.title2 || '');
      formDataToSend.append('subtitle2', formData.subtitle2 || '');
      formDataToSend.append('header', formData.header || '');
      formDataToSend.append('id', id);

      const response = await postData("Headercontent/update_webcontent", formDataToSend, true);
      if (response && response.status) {
        setMessage({ text: response.message || 'Updated successfully', type: 'success' });
        resetForm();
        await fetchContent();
      } else {
        setMessage({ text: response?.message || 'Failed to update content', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating content:', error);
      setMessage({ text: 'Server error while updating content: ' + error.message, type: 'error' });
    } finally {
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    setStatusLoading(id);
    try {
      const response = await postData("Headercontent/change_webcontent_status", { id, status: newStatus });
      if (response && response.status) {
        setMessage({ text: response.message || `Status ${newStatus === 1 ? 'Activated' : 'Deactivated'} successfully`, type: 'success' });
        await fetchContent();
      } else {
        setMessage({ text: response?.message || 'Failed to change status', type: 'error' });
      }
    } catch (error) {
      console.error('Error changing status:', error);
      setMessage({ text: 'Server error while changing status', type: 'error' });
    } finally {
      setStatusLoading(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    }
  };

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setShowForm(true);
    setFormData({
      video: null, poster: null,
      title1: item.title1 || '', subtitle1: item.subtitle1 || '',
      title2: item.title2 || '', subtitle2: item.subtitle2 || '',
      header: item.header || ''
    });
    setMessage({ text: `Editing ID: ${item.id}`, type: 'success' });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const handleCancelEdit = () => {
    resetForm();
    setMessage({ text: 'Edit cancelled', type: 'success' });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const handlePreview = (fileName) => {
    if (!fileName) {
      setMessage({ text: 'No file to preview', type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      return;
    }
    window.open(`${serverURL}/public/uploadnew/upload/${fileName}`, '_blank');
  };

  const handleVideoPlay = (id) => {
    if (playingVideo && playingVideo !== id) {
      const prevVideo = videoRefs.current[playingVideo];
      if (prevVideo) prevVideo.pause();
    }
    setPlayingVideo(playingVideo === id ? null : id);
  };

  // Cycle through available themes
  const cycleTheme = () => {
    const currentIndex = themes.indexOf(themeName);
    const nextIndex = (currentIndex + 1) % themes.length;
    changeTheme(themes[nextIndex]);
  };

  const styles = {
    container: {
      maxWidth: '1200px', margin: '0 auto', padding: '2rem 1.5rem',
      minHeight: '100vh', fontFamily: "'DM Sans', 'Inter', sans-serif",
      background: theme.bgPrimary, color: theme.textPrimary,
      transition: 'background 0.3s ease',
    },
    header: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'
    },
    heading: {
      fontWeight: '700', fontSize: '1.8rem', marginBottom: '0.25rem',
      display: 'flex', alignItems: 'center', gap: '12px',
      color: theme.textPrimary,
    },
    headingGradient: {
      background: theme.primaryGradient,
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
    },
    headingSmall: {
      fontSize: '0.85rem', fontWeight: '400', marginLeft: '8px',
      color: theme.textMuted, WebkitTextFillColor: theme.textMuted,
    },
    icon: { marginRight: '6px', WebkitTextFillColor: theme.primary },
    themeToggle: {
      background: theme.bgTertiary, border: `1px solid ${theme.border}`,
      borderRadius: '30px', padding: '0.5rem 1.2rem', color: theme.textPrimary,
      cursor: 'pointer', fontSize: '0.85rem', display: 'flex',
      alignItems: 'center', gap: '8px', transition: 'all 0.2s ease',
      fontFamily: 'inherit', fontWeight: 500,
    },
    addButton: {
      background: theme.primaryGradient, border: 'none', borderRadius: '30px',
      padding: '0.6rem 1.5rem', color: '#ffffff', cursor: 'pointer',
      fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px',
      transition: 'all 0.3s ease', fontWeight: '600',
      boxShadow: `0 4px 15px ${globalTheme.primary}40`, fontFamily: 'inherit',
    },
    card: {
      background: theme.bgCard, borderRadius: '20px', padding: '1.8rem 2rem',
      marginBottom: '2.5rem', boxShadow: theme.shadow,
      border: `1px solid ${theme.border}`, transition: 'all 0.3s ease',
    },
    cardHeader: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'
    },
    cardTitle: {
      fontWeight: '600', fontSize: '1.3rem', margin: 0, color: theme.textPrimary,
      display: 'flex', alignItems: 'center', gap: '8px',
    },
    formGrid: {
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem', marginBottom: '1.5rem'
    },
    fullWidth: { gridColumn: '1 / -1' },
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: {
      fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px',
      color: theme.textSecondary, fontWeight: '700',
    },
    fileInputWrapper: {
      background: theme.bgInput, borderRadius: '12px', padding: '0.5rem 1rem',
      border: `2px solid ${theme.border}`, display: 'flex', alignItems: 'center',
      transition: 'all 0.2s ease', cursor: 'pointer',
    },
    fileInput: {
      background: 'transparent', border: 'none', fontSize: '0.9rem',
      width: '100%', padding: '6px 0', cursor: 'pointer', color: theme.textPrimary,
      fontFamily: 'inherit',
    },
    textInput: {
      background: theme.bgInput, border: `2px solid ${theme.border}`,
      borderRadius: '12px', padding: '0.7rem 1rem', fontSize: '0.9rem',
      color: theme.textPrimary, transition: 'all 0.2s ease', outline: 'none',
      width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
    },
    textareaInput: {
      background: theme.bgInput, border: `2px solid ${theme.border}`,
      borderRadius: '12px', padding: '0.7rem 1rem', fontSize: '0.9rem',
      color: theme.textPrimary, transition: 'all 0.2s ease', outline: 'none',
      width: '100%', minHeight: '80px', resize: 'vertical', fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    fileInputHint: { fontSize: '0.75rem', marginTop: '4px', color: theme.textMuted },
    submitGroup: { display: 'flex', gap: '10px', marginTop: '0.5rem', flexWrap: 'wrap' },
    btn: {
      border: 'none', padding: '0.7rem 2rem', borderRadius: '40px', fontWeight: '600',
      fontSize: '0.95rem', display: 'inline-flex', alignItems: 'center', gap: '10px',
      cursor: 'pointer', transition: 'all 0.2s ease', background: theme.bgTertiary,
      color: theme.textPrimary, border: `1px solid ${theme.border}`, fontFamily: 'inherit',
    },
    btnPrimary: {
      background: theme.primaryGradient, borderColor: theme.primary,
      color: '#ffffff', boxShadow: `0 4px 15px ${globalTheme.primary}40`,
    },
    btnSuccess: {
      background: theme.success, borderColor: theme.success,
      color: '#ffffff', boxShadow: `0 4px 15px ${globalTheme.success}40`,
    },
    btnDanger: {
      background: theme.danger, borderColor: theme.danger,
      color: '#ffffff', boxShadow: `0 4px 15px ${globalTheme.error || globalTheme.danger}40`,
    },
    btnSecondary: {
      padding: '0.4rem 1.4rem', fontSize: '0.85rem',
      background: theme.bgTertiary, border: `1px solid ${theme.border}`,
    },
    btnDisabled: { opacity: '0.6', cursor: 'not-allowed' },
    btnToggle: {
      background: theme.bgTertiary, border: `1px solid ${theme.border}`,
      borderRadius: '30px', padding: '0.4rem 1.2rem', color: theme.textPrimary,
      cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex',
      alignItems: 'center', gap: '8px', transition: 'all 0.2s ease', fontFamily: 'inherit',
    },
    statusMessage: {
      marginTop: '16px', padding: '12px 20px', borderRadius: '12px',
      animation: 'slideIn 0.3s ease', background: theme.bgTertiary,
      color: theme.textPrimary, borderLeft: `4px solid ${theme.primary}`,
      fontSize: '0.9rem',
    },
    statusSuccess: {
      borderLeft: `4px solid ${theme.success}`,
      background: theme.successBg, color: theme.success,
    },
    statusError: {
      borderLeft: `4px solid ${theme.danger}`,
      background: theme.dangerBg, color: theme.danger,
    },
    tableWrapper: {
      overflowX: 'auto', borderRadius: '16px', background: theme.bgSecondary,
      border: `1px solid ${theme.border}`, boxShadow: theme.shadow,
    },
    table: {
      width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: '1000px'
    },
    th: {
      textAlign: 'left', padding: '0.8rem 1rem', fontWeight: '700',
      letterSpacing: '0.5px', background: theme.bgTertiary,
      color: theme.primary, borderBottom: `2px solid ${theme.border}`,
      whiteSpace: 'nowrap', fontSize: '0.75rem', textTransform: 'uppercase',
    },
    td: {
      padding: '0.8rem 1rem', verticalAlign: 'middle', color: theme.textPrimary,
      borderBottom: `1px solid ${theme.borderLight}`, maxWidth: '150px',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    trHover: { background: theme.bgHover },
    emptyCell: { textAlign: 'center', padding: '2.5rem 1rem', color: theme.textMuted },
    loadingCell: { textAlign: 'center', padding: '2.5rem 1rem', color: theme.textMuted },
    badgeId: {
      background: `${theme.primary}15`, padding: '2px 12px', borderRadius: '40px',
      fontWeight: '700', fontSize: '0.8rem', color: theme.primary,
      border: `1px solid ${theme.primary}30`, fontFamily: 'monospace',
    },
    previewBtn: {
      background: 'transparent', border: `1px solid ${theme.border}`,
      color: theme.textSecondary, cursor: 'pointer', fontSize: '0.85rem',
      padding: '4px 12px', transition: 'all 0.2s ease', borderRadius: '20px',
    },
    editBtn: {
      borderRadius: '30px', padding: '4px 14px', cursor: 'pointer',
      transition: 'all 0.2s ease', fontSize: '0.8rem', background: 'transparent',
      border: `1px solid ${theme.border}`, color: theme.textSecondary, fontFamily: 'inherit',
    },
    editBtnActive: {
      background: theme.primaryGradient, borderColor: theme.primary, color: '#ffffff',
    },
    statusBtn: {
      borderRadius: '30px', padding: '4px 14px', cursor: 'pointer',
      transition: 'all 0.2s ease', fontSize: '0.75rem', fontWeight: '700',
      border: 'none', minWidth: '80px', fontFamily: 'inherit',
    },
    statusActive: { background: theme.success, color: '#ffffff' },
    statusInactive: { background: theme.danger, color: '#ffffff' },
    statusLoading: {
      background: theme.bgTertiary, color: theme.textMuted,
      cursor: 'not-allowed', opacity: '0.6',
    },
    actionButtons: {
      display: 'flex', gap: '6px', alignItems: 'center',
      flexWrap: 'wrap', justifyContent: 'center',
    },
    spinner: { display: 'inline-block', animation: 'spin 1s linear infinite' },
    spinnerSmall: {
      display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: '0.8rem',
    },
    videoContainer: {
      position: 'relative', width: '120px', maxWidth: '100%', borderRadius: '10px',
      overflow: 'hidden', background: theme.videoBg, aspectRatio: '16/9',
      border: `2px solid ${theme.border}`, boxShadow: `0 4px 12px ${globalTheme.primary}15`,
    },
    videoElement: {
      width: '100%', height: '100%', objectFit: 'cover', display: 'block',
      cursor: 'pointer', background: theme.videoBg,
    },
    playButton: {
      position: 'absolute', top: '50%', left: '50%',
      transform: 'translate(-50%, -50%)',
      background: `${globalTheme.primary}e6`, border: '2px solid white',
      borderRadius: '50%', width: '40px', height: '40px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'white', fontSize: '16px', cursor: 'pointer',
      transition: 'all 0.3s ease', zIndex: 2,
      boxShadow: `0 4px 15px ${globalTheme.primary}60`,
    },
    posterImage: {
      width: '60px', height: '60px', objectFit: 'cover', borderRadius: '10px',
      border: `2px solid ${theme.border}`, boxShadow: `0 2px 8px ${globalTheme.primary}10`,
    },
    posterPlaceholder: {
      width: '60px', height: '60px', background: theme.bgTertiary, borderRadius: '10px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: theme.textMuted, fontSize: '0.65rem',
      border: `2px dashed ${theme.border}`, textAlign: 'center', padding: '4px',
    },
    mediaCell: { minWidth: '130px' },
    posterCell: { minWidth: '80px' },
    statusCell: { minWidth: '90px' },
    formContainer: { display: showForm ? 'block' : 'none', animation: 'slideIn 0.3s ease' },
    textTruncate: {
      maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis',
      whiteSpace: 'nowrap', display: 'inline-block',
    },
    dateText: { fontSize: '0.75rem', color: theme.textMuted },
    themeSelector: {
      display: 'flex', gap: '6px', alignItems: 'center',
      background: theme.bgTertiary, border: `1px solid ${theme.border}`,
      borderRadius: '30px', padding: '4px',
    },
    themeDot: (color, isActive) => ({
      width: '24px', height: '24px', borderRadius: '50%',
      background: color, cursor: 'pointer',
      border: isActive ? '3px solid white' : '2px solid transparent',
      boxShadow: isActive ? `0 0 0 2px ${color}` : 'none',
      transition: 'all 0.2s ease',
    }),
  };

  const PosterDisplay = ({ posterUrl, id }) => {
    const [imageError, setImageError] = useState(false);
    if (!posterUrl || imageError) {
      return <div style={styles.posterPlaceholder}>No poster</div>;
    }
    return (
      <img src={posterUrl} alt={`Poster ${id}`}
        style={styles.posterImage} onError={() => setImageError(true)} />
    );
  };

  const VideoDisplay = ({ videoUrl, id }) => {
    const [videoError, setVideoError] = useState(false);
    const isPlaying = playingVideo === id;
    if (!videoUrl || videoError) {
      return <div style={styles.posterPlaceholder}>No video</div>;
    }
    return (
      <div style={styles.videoContainer}>
        <video ref={el => videoRefs.current[id] = el} src={videoUrl}
          style={styles.videoElement} muted playsInline loop
          onClick={() => handleVideoPlay(id)} onError={() => setVideoError(true)} />
        {!isPlaying && (
          <div style={styles.playButton} onClick={() => handleVideoPlay(id)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.15)';
              e.currentTarget.style.boxShadow = `0 4px 20px ${globalTheme.primary}80`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
              e.currentTarget.style.boxShadow = `0 4px 15px ${globalTheme.primary}60`;
            }}>
            ▶
          </div>
        )}
        {isPlaying && (
          <div style={{
            ...styles.playButton,
            background: `${theme.danger}e6`,
            boxShadow: `0 4px 15px ${theme.danger}60`,
          }} onClick={() => handleVideoPlay(id)}>
            ❚❚
          </div>
        )}
      </div>
    );
  };

  // Theme color map for selector dots
  const themeColors = {
    blue: '#3b82f6', indigo: '#6366f1', purple: '#8b5cf6',
    teal: '#14b8a6', emerald: '#10b981', amber: '#f59e0b',
    slate: '#64748b', rose: '#f43f5e',
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        * { box-sizing: border-box; }
        input[type="file"]::file-selector-button {
          background: ${theme.bgTertiary}; border: none; padding: 6px 14px;
          border-radius: 40px; color: ${theme.primary}; font-weight: 600;
          margin-right: 12px; cursor: pointer; transition: all 0.2s ease;
          font-family: inherit;
        }
        input[type="file"]::file-selector-button:hover {
          background: ${theme.primary}; color: #fff;
        }
        video::-webkit-media-controls { display: none !important; }
        video::-webkit-media-controls-enclosure { display: none !important; }
        input, textarea { transition: all 0.2s ease; }
        ::-webkit-scrollbar { height: 8px; width: 8px; }
        ::-webkit-scrollbar-track { background: ${theme.scrollbarTrack}; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: ${theme.scrollbarThumb}; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: ${theme.scrollbarThumbHover}; }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.heading}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '12px',
            background: theme.primaryGradient, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 12px ${globalTheme.primary}40`,
          }}>
            <span style={{ fontSize: '22px', WebkitTextFillColor: 'white' }}>🎬</span>
          </div>
          <div>
            <span style={styles.headingGradient}>Web Content Manager</span>
            <br />
            <small style={styles.headingSmall}>Manage videos, posters & content</small>
          </div>
        </h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Theme Color Selector */}
          <div style={styles.themeSelector}>
            {themes.map(t => (
              <div key={t}
                style={styles.themeDot(themeColors[t] || globalTheme.primary, themeName === t)}
                onClick={() => changeTheme(t)}
                title={t.charAt(0).toUpperCase() + t.slice(1)}
              />
            ))}
          </div>
          {!showForm && !editingId && (
            <button style={styles.addButton} onClick={() => setShowForm(true)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 8px 25px ${globalTheme.primary}50`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 15px ${globalTheme.primary}40`;
              }}>
              <span>➕</span> Add New
            </button>
          )}
        </div>
      </div>

      {/* Add/Edit Content Form */}
      <div style={styles.formContainer}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '8px',
                background: `${theme.primary}15`, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: theme.primary, fontSize: '16px',
              }}>
                {editingId ? '✏️' : '📤'}
              </div>
              {editingId ? `Edit Content (ID: ${editingId})` : 'Add New Content'}
            </h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button style={styles.btnToggle} onClick={handleCancelEdit}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = theme.bgHover;
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.color = theme.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = theme.bgTertiary;
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.color = theme.textPrimary;
                }}>
                {showForm ? '➖ Close' : '➕ More'}
              </button>
            </div>
          </div>
          
          <form onSubmit={editingId ? (e) => { e.preventDefault(); handleUpdate(editingId); } : handleSubmit}>
            <div style={styles.formGrid}>
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>📝 Header</label>
                  <input type="text" name="header" value={formData.header}
                    onChange={handleInputChange} placeholder="Enter header text"
                    style={styles.textInput}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = theme.primary;
                      e.currentTarget.style.boxShadow = `0 0 0 3px ${globalTheme.primary}20`;
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = theme.border;
                      e.currentTarget.style.boxShadow = 'none';
                    }} />
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>📌 Title 1</label>
                <input type="text" name="title1" value={formData.title1}
                  onChange={handleInputChange} placeholder="Enter title 1"
                  style={styles.textInput}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${globalTheme.primary}20`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }} />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>📄 Subtitle 1</label>
                <input type="text" name="subtitle1" value={formData.subtitle1}
                  onChange={handleInputChange} placeholder="Enter subtitle 1"
                  style={styles.textInput}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${globalTheme.primary}20`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }} />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>📌 Title 2</label>
                <input type="text" name="title2" value={formData.title2}
                  onChange={handleInputChange} placeholder="Enter title 2"
                  style={styles.textInput}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${globalTheme.primary}20`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }} />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>📄 Subtitle 2</label>
                <input type="text" name="subtitle2" value={formData.subtitle2}
                  onChange={handleInputChange} placeholder="Enter subtitle 2"
                  style={styles.textInput}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${globalTheme.primary}20`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }} />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>🎥 Video File</label>
                <div style={styles.fileInputWrapper}>
                  <input type="file" id="videoInput" name="video" accept="video/*"
                    onChange={handleFileChange} style={styles.fileInput} />
                </div>
                {editingId && <div style={styles.fileInputHint}>Leave empty to keep existing video</div>}
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>🖼️ Poster Image</label>
                <div style={styles.fileInputWrapper}>
                  <input type="file" id="posterInput" name="poster" accept="image/*"
                    onChange={handleFileChange} style={styles.fileInput} />
                </div>
                {editingId && <div style={styles.fileInputHint}>Leave empty to keep existing poster</div>}
              </div>
            </div>

            <div style={styles.submitGroup}>
              <button type="submit"
                style={{
                  ...styles.btn,
                  ...(editingId ? styles.btnSuccess : styles.btnPrimary),
                  ...(submitting ? styles.btnDisabled : {})
                }}
                disabled={submitting}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                {submitting ? (
                  <><span style={styles.spinner}>⏳</span> Processing...</>
                ) : (
                  <><span>{editingId ? '💾' : '⬆️'}</span> {editingId ? 'Update' : 'Submit'}</>
                )}
              </button>
            </div>

            {message.text && (
              <div style={{
                ...styles.statusMessage,
                ...(message.type === 'success' ? styles.statusSuccess : {}),
                ...(message.type === 'error' ? styles.statusError : {})
              }}>
                {message.text}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Content List Table */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '8px',
              background: `${theme.primary}15`, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: theme.primary, fontSize: '16px',
            }}>
              📋
            </div>
            Content List
            <span style={{
              fontSize: '0.75rem', fontWeight: '600', marginLeft: '8px',
              color: theme.primary, background: `${theme.primary}15`,
              padding: '2px 10px', borderRadius: '20px',
              border: `1px solid ${theme.primary}30`,
            }}>
              {contentList.length} items
            </span>
          </h2>
          <button style={{ ...styles.btn, ...styles.btnSecondary }}
            onClick={fetchContent} disabled={loading}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme.bgHover;
              e.currentTarget.style.borderColor = theme.primary;
              e.currentTarget.style.color = theme.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.bgTertiary;
              e.currentTarget.style.borderColor = theme.border;
              e.currentTarget.style.color = theme.textPrimary;
            }}>
            <span style={loading ? styles.spinner : {}}>🔄</span> Refresh
          </button>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Header</th>
                <th style={styles.th}>Title 1</th>
                <th style={styles.th}>Subtitle 1</th>
                <th style={styles.th}>Title 2</th>
                <th style={styles.th}>Subtitle 2</th>
                <th style={styles.th}>Video</th>
                <th style={styles.th}>Poster</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Created</th>
                <th style={{...styles.th, textAlign: 'center'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" style={styles.loadingCell}>
                    <div style={{
                      width: '32px', height: '32px', border: `3px solid ${theme.border}`,
                      borderTopColor: theme.primary, borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
                    }} />
                    Loading content...
                  </td>
                </tr>
              ) : !contentList || contentList.length === 0 ? (
                <tr>
                  <td colSpan="11" style={styles.emptyCell}>
                    <div style={{
                      width: '64px', height: '64px', borderRadius: '50%',
                      background: `${theme.primary}15`, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 12px', fontSize: '28px',
                    }}>
                      ✨
                    </div>
                    <div style={{ fontWeight: 600, color: theme.textPrimary, marginBottom: '4px' }}>
                      No content found
                    </div>
                    Click "Add New" to create your first entry!
                  </td>
                </tr>
              ) : (
                contentList.map((item) => {
                  const videoUrl = item.video ? `${serverURL}/public/uploadnew/upload/${item.video}` : null;
                  const posterUrl = item.poster ? `${serverURL}/public/uploadnew/upload/${item.poster}` : null;
                  const createdDate = item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A';
                  const isActive = item.status === 1;
                  const isStatusLoading = statusLoading === item.id;

                  return (
                    <tr key={item.id}
                      style={{
                        ...(editingId === item.id ? styles.trHover : {}),
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (editingId !== item.id) {
                          e.currentTarget.style.background = theme.bgHover;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (editingId !== item.id) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}>
                      <td style={styles.td}>
                        <span style={styles.badgeId}>#{item.id}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.textTruncate} title={item.header || ''}>
                          {item.header || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.textTruncate} title={item.title1 || ''}>
                          {item.title1 || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.textTruncate} title={item.subtitle1 || ''}>
                          {item.subtitle1 || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.textTruncate} title={item.title2 || ''}>
                          {item.title2 || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.textTruncate} title={item.subtitle2 || ''}>
                          {item.subtitle2 || '—'}
                        </span>
                      </td>
                      <td style={{...styles.td, ...styles.mediaCell}}>
                        <VideoDisplay videoUrl={videoUrl} id={item.id} />
                      </td>
                      <td style={{...styles.td, ...styles.posterCell}}>
                        <PosterDisplay posterUrl={posterUrl} id={item.id} />
                      </td>
                      <td style={{...styles.td, ...styles.statusCell}}>
                        <button
                          style={{
                            ...styles.statusBtn,
                            ...(isStatusLoading ? styles.statusLoading : {}),
                            ...(isActive ? styles.statusActive : styles.statusInactive)
                          }}
                          onClick={() => handleStatusToggle(item.id, item.status)}
                          disabled={isStatusLoading}
                          onMouseEnter={(e) => {
                            if (!isStatusLoading) e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}>
                          {isStatusLoading ? (
                            <span style={styles.spinnerSmall}>⏳</span>
                          ) : (
                            isActive ? '✅ Active' : '❌ Inactive'
                          )}
                        </button>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.dateText}>{createdDate}</span>
                      </td>
                      <td style={{...styles.td, textAlign: 'center'}}>
                        <div style={styles.actionButtons}>
                          <button
                            style={{
                              ...styles.editBtn,
                              ...(editingId === item.id ? styles.editBtnActive : {})
                            }}
                            onClick={() => handleEditClick(item)}
                            onMouseEnter={(e) => {
                              if (editingId !== item.id) {
                                e.currentTarget.style.background = `${theme.primary}15`;
                                e.currentTarget.style.borderColor = theme.primary;
                                e.currentTarget.style.color = theme.primary;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (editingId !== item.id) {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.borderColor = theme.border;
                                e.currentTarget.style.color = theme.textSecondary;
                              }
                            }}>
                            {editingId === item.id ? '📝 Editing' : '✏️ Edit'}
                          </button>
                          {videoUrl && (
                            <button style={styles.previewBtn}
                              onClick={() => handlePreview(item.video)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = theme.primary;
                                e.currentTarget.style.color = '#ffffff';
                                e.currentTarget.style.borderColor = theme.primary;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = theme.textSecondary;
                                e.currentTarget.style.borderColor = theme.border;
                              }}>
                              🔗
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WebContentManager;